// MediaPipe 动态加载器，用于处理不同环境下的模块加载问题
export class MediaPipeLoader {
  static loaded = false;
  static loadingPromise = null;

  static async load() {
    // 首先尝试动态导入（ES 模块方式）
    try {
      const [{ Hands }, { Camera }] = await Promise.all([
        import('@mediapipe/hands'),
        import('@mediapipe/camera_utils')
      ]);
      
      // 测试 Hands 是否确实是一个构造函数
      if (typeof Hands === 'function') {
        // 尝试创建实例以确保它是有效的构造函数
        try {
          const testInstance = new Hands({});
          testInstance.close && testInstance.close(); // 清理测试实例
          return { Hands, Camera };
        } catch (testError) {
          console.warn('Hands 构造函数测试失败:', testError);
        }
      }
    } catch (esmError) {
      console.warn('ES 模块导入失败，尝试 CDN 方式:', esmError);
    }

    // 如果动态导入失败，再尝试 CDN 方式
    return this.loadViaCDN();
  }

  static async loadViaCDN() {
    // 如果已经在加载中，返回同一个 promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // 如果已经加载过，直接返回
    if (this.loaded && window.MediaPipeHands && window.MediaPipeCameraUtils) {
      return {
        Hands: window.MediaPipeHands,
        Camera: window.MediaPipeCameraUtils
      };
    }

    this.loadingPromise = this._loadScriptsViaCDN();
    const result = await this.loadingPromise;
    this.loaded = true;
    return result;
  }

  static _loadScriptsViaCDN() {
    return new Promise((resolve, reject) => {
      // 检查是否已经在页面中加载
      if (window.hands && window.hands.Hands && window.CameraUtils && window.CameraUtils.Camera) {
        resolve({
          Hands: window.hands.Hands,
          Camera: window.CameraUtils.Camera
        });
        return;
      }

      // 加载 hands 脚本
      const loadHands = () => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands/hands.js';
        script.async = false; // 顺序很重要
        
        script.onload = () => {
          console.log('Hands script loaded, available globals:', Object.keys(window).filter(key => key.toLowerCase().includes('hand')));
          
          // 检查实际的全局对象结构
          let HandsConstructor = null;
          if (window.hands && window.hands.Hands) {
            HandsConstructor = window.hands.Hands;
          } else if (window.Hands) {
            HandsConstructor = window.Hands;
          } else {
            // 搜索可能的变量名
            for (const key in window) {
              if (key.includes('Hands') && typeof window[key] === 'function') {
                HandsConstructor = window[key];
                break;
              }
            }
          }
          
          if (HandsConstructor) {
            window.MediaPipeHands = HandsConstructor;
            // 然后加载 camera_utils
            loadCameraUtils();
          } else {
            console.warn('Hands constructor not found, checking alternative structures...');
            // 尝试一些已知的替代结构
            if (window.MP_HANDS && typeof window.MP_HANDS === 'function') {
              window.MediaPipeHands = window.MP_HANDS;
              loadCameraUtils();
            } else if (window.Hands && typeof window.Hands === 'object' && window.Hands.Hands && typeof window.Hands.Hands === 'function') {
              window.MediaPipeHands = window.Hands.Hands;
              loadCameraUtils();
            } else {
              reject(new Error('Hands constructor not found after script load'));
            }
          }
        };
        
        script.onerror = (error) => {
          reject(new Error(`Hands 脚本加载失败: ${error.message || 'Unknown error'}`));
        };
        
        document.head.appendChild(script);
      };

      const loadCameraUtils = () => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js';
        script.async = false; // 顺序很重要
        
        script.onload = () => {
          console.log('CameraUtils script loaded, available globals:', Object.keys(window).filter(key => key.toLowerCase().includes('camera')));
          
          // 检查实际的全局对象结构
          let CameraConstructor = null;
          if (window.CameraUtils && window.CameraUtils.Camera) {
            CameraConstructor = window.CameraUtils.Camera;
          } else if (window.Camera) {
            CameraConstructor = window.Camera;
          } else {
            // 搜索可能的变量名
            for (const key in window) {
              if (key.includes('Camera') && typeof window[key] === 'function') {
                CameraConstructor = window[key];
                break;
              }
            }
          }
          
          if (CameraConstructor) {
            window.MediaPipeCameraUtils = CameraConstructor;
          } else {
            console.warn('Camera constructor not found, checking alternative structures...');
            // 尝试一些已知的替代结构
            if (window.MP_CAMERA && typeof window.MP_CAMERA === 'function') {
              window.MediaPipeCameraUtils = window.MP_CAMERA;
            } else if (window.CameraUtils && typeof window.CameraUtils === 'object' && window.CameraUtils.Camera && typeof window.CameraUtils.Camera === 'function') {
              window.MediaPipeCameraUtils = window.CameraUtils.Camera;
            } else {
              reject(new Error('Camera constructor not found after script load'));
            }
          }
          
          // 检查是否都已加载成功
          if (window.MediaPipeHands && window.MediaPipeCameraUtils) {
            resolve({
              Hands: window.MediaPipeHands,
              Camera: window.MediaPipeCameraUtils
            });
          } else {
            reject(new Error('MediaPipe 模块加载完成但构造函数不可用'));
          }
        };
        
        script.onerror = (error) => {
          reject(new Error(`Camera Utils 脚本加载失败: ${error.message || 'Unknown error'}`));
        };
        
        document.head.appendChild(script);
      };

      // 开始加载
      loadHands();
    });
  }
}